import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import {
  CheckIcon,
  CurrencyIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import {
  ChargePaymentStatus,
  PaymentDisplay_Booking$key,
} from "./__generated__/PaymentDisplay_Booking.graphql";

const paidStatuses: ChargePaymentStatus[] = ["PAID", "PARTIALLY_PAID"];

export const PaymentDisplay = ({
  bookingRef,
}: {
  bookingRef: PaymentDisplay_Booking$key | null;
}) => {
  const booking = useFragment(PaymentDisplayFragmentString, bookingRef);

  const hasBookingRate = !!booking?.rate?.id;
  const hasPlayerRates = !!booking?.rounds?.edges?.some(
    (round) => !!round?.node?.roundRate?.rate?.id
  );
  const isPrivateInviteFlow =
    booking?.rounds?.edges?.some(
      (round) => round?.node?.__typename === "MemberRound"
    ) ?? false;

  const allRoundsPaid =
    !hasPlayerRates ||
    booking?.rounds?.edges?.every((round) =>
      paidStatuses.includes(round?.node?.paymentStatus ?? "PAID")
    );
  const allBookingChargesPaid =
    !hasBookingRate || paidStatuses.includes(booking?.paymentStatus ?? "PAID");

  const allChargesPaid = allRoundsPaid && allBookingChargesPaid;

  if (isPrivateInviteFlow) {
    return null;
  }

  return (
    <VStack
      gap={5}
      padding={5}
      paddingTop={6}
      backgroundColor="surface-primary"
    >
      <Text variant="mainHeading">{t`Payment`}</Text>
      {allChargesPaid ? (
        <AllChargesPaidDisplay />
      ) : (
        <PaymentDueUponArrivalDisplay />
      )}
    </VStack>
  );
};

const AllChargesPaidDisplay = () => {
  return (
    <HStack alignItems="center" gap={4}>
      <Box
        width={32}
        height={32}
        style={{ borderRadius: 32 }}
        backgroundColor="surface-main-primary-subtlest"
        padding={2}
      >
        <CheckIcon color="icon-primary" />
      </Box>
      <Text variant="mainSmallSemiBold" color="text-main-primary">
        {t`All charges have been paid.`}
      </Text>
    </HStack>
  );
};

const PaymentDueUponArrivalDisplay = () => {
  return (
    <HStack alignItems="center" gap={4}>
      <Box
        width={32}
        height={32}
        style={{ borderRadius: 32 }}
        backgroundColor="surface-secondary"
        padding={2}
      >
        <CurrencyIcon />
      </Box>
      <Text variant="mainSmallSemiBold">
        {t`All charges are due upon arrival.`}
      </Text>
    </HStack>
  );
};

const PaymentDisplayFragmentString = graphql`
  fragment PaymentDisplay_Booking on Booking {
    ... on StandardBooking {
      rate {
        id
      }
      paymentStatus
      rounds {
        edges {
          node {
            __typename
            roundRate {
              rate {
                id
              }
            }
            paymentStatus
          }
        }
      }
    }
  }
`;
